import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/errors";
import SportTabs from "../../components/terminal/SportTabs";
import {
  Cell,
  EmptyState,
  HeadCell,
  MatchupCell,
  StartTimeCell,
  trapTone,
} from "../../components/terminal/DataTable";

function TrapMeter({ score }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div className="w-24">
      <div className="flex items-baseline justify-between mono text-[10px] mb-0.5">
        <span className={trapTone(s)}>{s}</span>
        <span className="text-zinc-600">/100</span>
      </div>
      <div className="h-1 bg-zinc-900 border border-zinc-800">
        <div
          className={s >= 75 ? "h-full bg-red-500/70" : s >= 60 ? "h-full bg-[#FFBF00]/70" : "h-full bg-zinc-600/50"}
          style={{ width: `${s}%` }}
        />
      </div>
    </div>
  );
}

export default function Traps() {
  const [rows, setRows] = useState(null);
  const [sport, setSport] = useState("");

  const load = useCallback(async (s) => {
    try {
      const params = s ? { sport: s } : {};
      const { data } = await api.get("/terminal/traps", { params });
      setRows(data.rows || []);
    } catch (err) {
      console.warn("traps load failed", err);
      toast.error(apiErrorMessage(err, "Could not load traps"));
      setRows([]);
    }
  }, []);

  useEffect(() => { load(sport); }, [sport, load]);

  return (
    <div data-testid="traps-page">
      <div className="mb-4">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <AlertTriangle size={20} className="amber-text" />
          Public trap detector
        </h2>
        <p className="mono text-[11px] text-zinc-500 mt-1">
          Games where the public is loading up on one side ≥ 60% while sharps
          are positioned the other way. Trap score ≥ 60 = fade the public.
        </p>
      </div>

      <SportTabs value={sport} onChange={setSport} testidPrefix="traps-sport" />

      {rows === null && (
        <div className="py-8 text-center mono text-xs text-zinc-500">
          <Loader2 size={16} className="inline animate-spin mr-2" />
          Loading traps…
        </div>
      )}
      {rows && rows.length === 0 && (
        <EmptyState
          testid="traps-empty"
          message="No traps flagged on the evaluated slate. The public is split or our model agrees with the sharp side."
        />
      )}

      {rows && rows.length > 0 && (
        <div className="border border-zinc-800 overflow-x-auto" data-testid="traps-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr>
                <HeadCell>START</HeadCell>
                <HeadCell>MATCHUP</HeadCell>
                <HeadCell>PUBLIC SIDE</HeadCell>
                <HeadCell>SHARP SIDE</HeadCell>
                <HeadCell>TRAP</HeadCell>
                <HeadCell>LEAN (FADE)</HeadCell>
                <HeadCell>DQ</HeadCell>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="hover:bg-zinc-900/40" data-testid={`trap-row-${r.key}`}>
                  <Cell><StartTimeCell iso={r.start_time} /></Cell>
                  <Cell><MatchupCell row={r} /></Cell>
                  <Cell className="text-[#FFBF00]">{(r.public_side || "—").toUpperCase()}</Cell>
                  <Cell className="text-[#00D4FF]">{(r.sharp_side || "—").toUpperCase()}</Cell>
                  <Cell><TrapMeter score={r.trap_score} /></Cell>
                  <Cell className="text-[#00D4FF]">{r.lean || "—"}</Cell>
                  <Cell className="text-zinc-400">{r.data_quality_score ?? 0}</Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
